/**
 * offlineDb.js — minimal promise-based IndexedDB wrapper for offline upload support.
 *
 * Dependency-free (no Dexie) so it needs no `npm install`. Stores in-progress
 * "inspection report drafts" — the full form snapshot plus the raw photo Blobs —
 * so a Monthly Inspection Report captured while offline survives a tab close and
 * is auto-synced when connectivity returns.
 *
 * Draft record shape:
 * {
 *   id,             // string uuid (primary key)
 *   propertyId,     // string — the property the report belongs to
 *   clientUuid,     // string uuid — server-side idempotency key (dedup)
 *   status,         // 'draft' | 'queued' | 'syncing' | 'failed'
 *   permanent,      // boolean — true once a non-retryable error (e.g. 422) was hit
 *   formData,       // full form snapshot. Offline photo fields hold a
 *                   //   `__offline__:<field>` placeholder until synced.
 *   photoRotations, // { field: degrees }
 *   locations,      // string[] — custom checklist location names
 *   photos,         // { field: { blob, fileName, mimeType, s3Key? } }
 *   attempts,       // number of sync attempts
 *   lastError,      // string | null
 *   updatedAt,      // ISO string
 * }
 */

const DB_NAME = "gg_offline";
const DB_VERSION = 1;
const STORE = "inspectionDrafts";

export const OFFLINE_PHOTO_PREFIX = "__offline__:";
/** After this many consecutive transient failures, hold for manual retry. */
export const MAX_SYNC_ATTEMPTS = 5;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available in this browser"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("propertyId", "propertyId", { unique: false });
        store.createIndex("status", "status", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

/**
 * Probe whether offline storage can actually be used right now. Returns true if
 * IndexedDB opens and a trivial write succeeds. Used to BLOCK an offline capture
 * with a clear message rather than silently losing a photo.
 */
export async function isOfflineStorageAvailable() {
  try {
    await openDb();
    // A read-only transaction is enough to confirm the store is usable.
    await tx("readonly", (store) => reqToPromise(store.count()));
    return true;
  } catch (e) {
    console.warn("[offlineDb] storage unavailable:", e && e.message);
    return false;
  }
}

/** True if an error is an IndexedDB quota/space failure (device full). */
export function isQuotaError(err) {
  if (!err) return false;
  const name = err.name || "";
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    /quota/i.test(err.message || "")
  );
}

function tx(mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const store = transaction.objectStore(STORE);
        let result;
        Promise.resolve(fn(store))
          .then((r) => {
            result = r;
          })
          .catch(reject);
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
      })
  );
}

function reqToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Insert or update a draft. Stamps updatedAt. Throws on quota/storage failure. */
export async function saveDraft(draft) {
  const record = { ...draft, updatedAt: new Date().toISOString() };
  await tx("readwrite", (store) => reqToPromise(store.put(record)));
  return record;
}

export async function getDraft(id) {
  return tx("readonly", (store) => reqToPromise(store.get(id)));
}

export async function getAllDrafts() {
  return tx("readonly", (store) => reqToPromise(store.getAll()));
}

/**
 * Find the most-recently-updated unsubmitted *create* draft for a property
 * (status 'draft' or held 'failed', and NOT an edit draft), so the create
 * screen can resume it.
 */
export async function getResumableDraftByProperty(propertyId, reportType = "monthly") {
  const all = await getAllDrafts();
  const pid = String(propertyId);
  return (
    all
      .filter(
        (d) =>
          String(d.propertyId) === pid &&
          !d.reportId &&
          (d.reportType || "monthly") === reportType &&
          (d.status === "draft" || d.status === "failed")
      )
      .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""))[0] ||
    null
  );
}

/**
 * Find the unsubmitted *edit* draft for a specific report, so the edit screen
 * can resume it. `reportType` disambiguates monthly vs initial reports that may
 * share the same numeric id across their separate tables.
 */
export async function getResumableDraftByReport(reportId, reportType = "monthly") {
  const all = await getAllDrafts();
  const rid = String(reportId);
  return (
    all
      .filter(
        (d) =>
          String(d.reportId) === rid &&
          (d.reportType || "monthly") === reportType &&
          (d.status === "draft" || d.status === "failed")
      )
      .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""))[0] ||
    null
  );
}

/**
 * Drafts eligible for automatic sync: queued, or failed transiently and still
 * under the attempt cap. Permanent failures (422 etc.) are excluded — they wait
 * for an explicit manual retry.
 */
export async function getSyncableDrafts() {
  const all = await getAllDrafts();
  return all.filter(
    (d) =>
      !d.permanent &&
      (d.status === "queued" ||
        (d.status === "failed" && (d.attempts || 0) < MAX_SYNC_ATTEMPTS))
  );
}

/** Count of drafts still holding un-synced data (for the global badge). */
export async function getPendingCount() {
  const all = await getAllDrafts();
  return all.filter((d) => d.status !== "draft").length;
}

/** Drafts that need explicit user attention (permanent fail or attempt cap hit). */
export async function getAttentionDrafts() {
  const all = await getAllDrafts();
  return all.filter(
    (d) =>
      d.permanent ||
      (d.status === "failed" && (d.attempts || 0) >= MAX_SYNC_ATTEMPTS)
  );
}

export async function deleteDraft(id) {
  await tx("readwrite", (store) => reqToPromise(store.delete(id)));
}

/** Wipe the entire offline queue (all pending drafts). */
export async function clearAllDrafts() {
  await tx("readwrite", (store) => reqToPromise(store.clear()));
}
